import { redirect, useLoaderData, useNavigate } from 'react-router';
import { Card, CardContent } from '@/components/ui/card';
import {
  MapPin,
  ChevronRight,
  Building2,
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
} from 'lucide-react';
import { clubApi, contactApi } from '@repo/api-client';
import { urlControlPanel } from '@/urls';
import { Image } from '@repo/ui/components/image';
import type { Club } from '@repo/shared-types';
import { Tag } from '@repo/ui/components/tag';
import { Button } from '@repo/ui/components/button';
import { useState } from 'react';

function ClubSelector() {
  const { clubs } = useLoaderData() as { clubs: Club[] };
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleClubSelect = (club: Club) => {
    navigate(`/club/${club.id}`, { viewTransition: true });
  };

  const handleContactRequest = async () => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await contactApi.contact('list-my-club');
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!clubs || clubs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 flex items-center justify-center">
        <div className="mx-auto max-w-2xl w-full">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="rounded-full bg-blue-100 p-4">
                    <Building2 className="h-12 w-12 text-blue-600" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h1 className="text-3xl font-bold text-gray-900">No hay clubes disponibles</h1>
                  <p className="text-gray-600 text-lg">
                    Actualmente no tienes acceso a ningún club en Playpadel.
                  </p>
                </div>

                {submitStatus === 'success' ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <p className="font-medium">¡Solicitud enviada correctamente!</p>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Nos pondremos en contacto contigo pronto para ayudarte a registrar tu club.
                    </p>
                  </div>
                ) : submitStatus === 'error' ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-red-600">
                      <AlertCircle className="h-5 w-5" />
                      <p className="font-medium">Error al enviar la solicitud</p>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Por favor, intenta nuevamente más tarde.
                    </p>
                    <Button onClick={handleContactRequest} disabled={isSubmitting} className="mt-4">
                      <RefreshCcw className="w-4 h-4" />
                      Intentar nuevamente
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <p className="text-gray-700">¿Quieres tener tu club en Playpadel?</p>

                    <Button
                      onClick={handleContactRequest}
                      disabled={isSubmitting}
                      className="w-full md:w-auto px-8 py-6 mx-auto"
                    >
                      Quiero tener mi club en Playpadel
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Multiple clubs - show visual selector
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="mx-auto max-w-4xl py-8">
        <div className="mb-8 space-y-2 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Selecciona tu club</h1>
          <p className="text-gray-600">Elige el club con el que deseas trabajar hoy</p>
        </div>

        <div className="grid gap-4 md:gap-6">
          {clubs.map((club: Club) => (
            <Card
              key={club.id}
              className="group cursor-pointer border-0 shadow-md transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
              onClick={() => handleClubSelect(club)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Image src={club.logo || ''} alt={club.name} className="max-w-12 max-h-12" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                        {club.name}
                      </h3>
                      <div className="mt-1 flex items-center space-x-4">
                        {club.city && (
                          <div className="flex items-center space-x-1 text-gray-500">
                            <MapPin className="h-4 w-4" />
                            <span className="text-sm">{club.city}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {club.role && <Tag variant="primary">{club.role}</Tag>}
                    <ChevronRight className="h-5 w-5 text-gray-400 transition-colors group-hover:text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 rounded-full bg-white/50 px-4 py-2 text-sm text-gray-600">
            <Building2 className="h-4 w-4" />
            <span>
              {clubs.length} club{clubs.length !== 1 ? 'es' : ''} disponible
              {clubs.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const loader = async () => {
  const clubs = await clubApi.getAll();
  if (clubs.results.length === 1) {
    return redirect(urlControlPanel(clubs.results[0].id));
  }
  return { clubs: clubs.results };
};

export const route = {
  index: true,
  element: <ClubSelector />,
  loader,
};
